import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { acceptInvitationAsExistingUser } from '@/api/invitations/invitationsApi';
import type { InvitationPreview } from '@/api/invitations/invitationsApi.types';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { BrandLogo } from '@/components/common/BrandLogo';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { LegalConsentCheckbox } from '@/components/legal/LegalConsentCheckbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/passwordInput';
import { PhoneInput } from '@/components/ui/phoneInput';
import { useAcceptInvitationMutation } from '@/hooks/useAcceptInvitationMutation';
import { useInvitationPreviewQuery } from '@/hooks/useInvitationPreviewQuery';
import { useAuth } from '@/hooks/useAuth';
import { LEGAL_CONSENT_REQUIRED_KEYS } from '@/constants/legal';
import { resolveHomeRoute } from '@/lib/resolveHomeRoute';
import { resolveLegalAudienceForInvitation } from '@/lib/resolveLegalAudience';
import { isBlankOrValidPhoneNumber } from '@/utils/phoneNumbers';
import { isSupportedLanguage } from '@/i18n/supportedLanguages';
import { readStoredLanguage } from '@/lib/languageStorage';
import { InvitingWorkspaceLine } from './InvitingWorkspaceLine';

const MINIMUM_PASSWORD_LENGTH = 10;

export function AcceptInvitationScreen() {
  const [searchParameters] = useSearchParams();
  const invitationToken = searchParameters.get('token');
  const resolvedToken = invitationToken === null ? '' : invitationToken;

  const invitationPreviewQuery = useInvitationPreviewQuery(resolvedToken);
  const previewData = invitationPreviewQuery.data;

  useEffect(
    function showInvitationInInviteeLanguage() {
      if (previewData === undefined || readStoredLanguage() !== null) {
        return;
      }
      if (isSupportedLanguage(previewData.language) && i18next.language !== previewData.language) {
        void i18next.changeLanguage(previewData.language);
      }
    },
    [previewData]
  );

  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-[2.5rem] shadow-xl overflow-hidden">
        <div className="p-8 md:p-10">
          <div className="flex justify-center mb-6">
            <BrandLogo className="h-10 w-auto object-contain" />
          </div>

          {resolvedToken.length === 0 || invitationPreviewQuery.isError === true ? (
            <InvalidInvitationMessage
              detail={resolveApiErrorMessage(invitationPreviewQuery.error)}
            />
          ) : invitationPreviewQuery.isLoading === true || previewData === undefined ? (
            <div className="py-12 flex justify-center">
              <LoadingSpinner className="w-7 h-7" />
            </div>
          ) : previewData.accountExists === true ? (
            <ExistingAccountAcceptance token={resolvedToken} preview={previewData} />
          ) : (
            <NewAccountRegistration token={resolvedToken} preview={previewData} />
          )}
        </div>
      </div>
    </div>
  );
}

interface AcceptanceProps {
  token: string;
  preview: InvitationPreview;
}

function NewAccountRegistration({ token, preview }: AcceptanceProps) {
  const { t } = useTranslation(['auth', 'admin', 'legal']);
  const legalAudience = resolveLegalAudienceForInvitation(preview.scopeCode, preview.isOwner);
  const navigate = useNavigate();
  const { completeSessionWithTokens } = useAuth();
  const acceptInvitationMutation = useAcceptInvitationMutation();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isPhoneValidationVisible, setIsPhoneValidationVisible] = useState(false);

  useEffect(
    function prefillDetailsFromPreview() {
      if (preview.fullName !== null) {
        setFullName(preview.fullName);
      }
      if (preview.phone !== null) {
        setPhone(preview.phone);
      }
    },
    [preview]
  );

  async function submitAcceptance(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setErrorMessage('');
    if (fullName.trim().length === 0 || password.length < MINIMUM_PASSWORD_LENGTH) {
      setErrorMessage(t('auth:acceptInvitation.validationHint'));
      return;
    }
    if (isBlankOrValidPhoneNumber(phone) === false) {
      setIsPhoneValidationVisible(true);
      return;
    }
    if (hasAcceptedTerms === false) {
      setErrorMessage(t(LEGAL_CONSENT_REQUIRED_KEYS[legalAudience], { ns: 'legal' }));
      return;
    }
    try {
      const acceptedInvitation = await acceptInvitationMutation.mutateAsync({
        token,
        fullName: fullName.trim(),
        phone: phone.trim().length === 0 ? null : phone.trim(),
        password,
        preferredLanguage: i18next.language,
        acceptTerms: hasAcceptedTerms,
      });
      await completeSessionWithTokens(acceptedInvitation.tokens);
      navigate('/', { replace: true });
    } catch (acceptError) {
      const backendMessage = resolveApiErrorMessage(acceptError);
      setErrorMessage(backendMessage.length > 0 ? backendMessage : t('admin:errors.generic'));
    }
  }

  return (
    <>
      <div className="text-center mb-8">
        <InvitingWorkspaceLine workspaceName={preview.workspaceName} />
        <h1 className="text-2xl font-bold text-zooindigo">{t('auth:acceptInvitation.title')}</h1>
        <p className="text-sm text-zoomuted mt-2">
          {t('auth:acceptInvitation.subtitle', { email: preview.email })}
        </p>
      </div>

      <form onSubmit={submitAcceptance} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="accept-name">{t('auth:acceptInvitation.fullNameLabel')}</Label>
          <Input
            id="accept-name"
            required
            value={fullName}
            onChange={function updateName(changeEvent) {
              setFullName(changeEvent.target.value);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="accept-phone">{t('auth:acceptInvitation.phoneLabel')}</Label>
          <PhoneInput
            inputId="accept-phone"
            value={phone}
            isValidationVisible={isPhoneValidationVisible}
            placeholder={t('auth:acceptInvitation.phonePlaceholder')}
            onChange={setPhone}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="accept-password">{t('auth:acceptInvitation.passwordLabel')}</Label>
          <PasswordInput
            id="accept-password"
            required
            value={password}
            placeholder={t('auth:passwordPlaceholder')}
            onChange={function updatePassword(changeEvent) {
              setPassword(changeEvent.target.value);
            }}
          />
          <p className="text-[11px] text-zoomuted">
            {t('auth:acceptInvitation.passwordHint', { count: MINIMUM_PASSWORD_LENGTH })}
          </p>
        </div>

        <LegalConsentCheckbox
          audience={legalAudience}
          isChecked={hasAcceptedTerms}
          onChange={setHasAcceptedTerms}
        />

        <FormError message={errorMessage} />

        <Button
          type="submit"
          size="large"
          className="w-full"
          isLoading={acceptInvitationMutation.isPending}
        >
          {t('auth:acceptInvitation.submit')}
        </Button>
      </form>
    </>
  );
}

function ExistingAccountAcceptance({ token, preview }: AcceptanceProps) {
  const { t } = useTranslation(['auth', 'admin', 'common', 'legal']);
  const legalAudience = resolveLegalAudienceForInvitation(preview.scopeCode, preview.isOwner);
  const navigate = useNavigate();
  const {
    status,
    currentUser,
    platformPermissions,
    logInWithCredentials,
    switchWorkspace,
    logOut,
  } = useAuth();

  const [password, setPassword] = useState('');
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const isSignedInAsInvitee =
    status === 'authenticated' &&
    currentUser !== null &&
    currentUser.email.trim().toLowerCase() === preview.email.trim().toLowerCase();
  const isSignedInAsOther =
    status === 'authenticated' && currentUser !== null && isSignedInAsInvitee === false;

  function confirmTermsAccepted(): boolean {
    if (hasAcceptedTerms === true) {
      return true;
    }
    setErrorMessage(t(LEGAL_CONSENT_REQUIRED_KEYS[legalAudience], { ns: 'legal' }));
    return false;
  }

  async function grantAndEnter() {
    const membership = await acceptInvitationAsExistingUser(token, hasAcceptedTerms);
    await switchWorkspace({ scopeCode: membership.scopeCode, tenantId: membership.tenantId });
    navigate(resolveHomeRoute(membership.scopeCode, platformPermissions), {
      replace: true,
    });
  }

  async function acceptWhileSignedIn() {
    setErrorMessage('');
    if (confirmTermsAccepted() === false) {
      return;
    }
    setIsWorking(true);
    try {
      await grantAndEnter();
    } catch (acceptError) {
      const backendMessage = resolveApiErrorMessage(acceptError);
      setErrorMessage(backendMessage.length > 0 ? backendMessage : t('admin:errors.generic'));
    } finally {
      setIsWorking(false);
    }
  }

  async function signInThenAccept(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setErrorMessage('');
    if (confirmTermsAccepted() === false) {
      return;
    }
    setIsWorking(true);
    try {
      await logInWithCredentials(preview.email, password);
      await grantAndEnter();
    } catch (acceptError) {
      const backendMessage = resolveApiErrorMessage(acceptError);
      setErrorMessage(backendMessage.length > 0 ? backendMessage : t('admin:errors.generic'));
    } finally {
      setIsWorking(false);
    }
  }

  if (isSignedInAsOther === true) {
    return (
      <div className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-zoowarningbg flex items-center justify-center mx-auto mb-4">
          <Icon name="AlertCircle" className="text-xl text-zoowarningtext" />
        </div>
        <h1 className="text-xl font-bold text-zooindigo">
          {t('auth:acceptInvitation.wrongAccountTitle')}
        </h1>
        <p className="text-sm text-zoomuted mt-2">
          {t('auth:acceptInvitation.wrongAccountDescription', { email: preview.email })}
        </p>
        <Button
          size="large"
          className="w-full mt-6"
          onClick={function handleLogout() {
            logOut();
          }}
        >
          {t('common:actions.logout')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="text-center mb-8">
        <InvitingWorkspaceLine workspaceName={preview.workspaceName} />
        <h1 className="text-2xl font-bold text-zooindigo">
          {t('auth:acceptInvitation.existingTitle')}
        </h1>
        <p className="text-sm text-zoomuted mt-2">
          {isSignedInAsInvitee === true
            ? t('auth:acceptInvitation.existingSubtitle', { email: preview.email })
            : t('auth:acceptInvitation.signInSubtitle', { email: preview.email })}
        </p>
      </div>

      {isSignedInAsInvitee === true ? (
        <div className="space-y-4">
          <LegalConsentCheckbox
            audience={legalAudience}
            isChecked={hasAcceptedTerms}
            onChange={setHasAcceptedTerms}
          />
          <FormError message={errorMessage} />
          <Button
            size="large"
            className="w-full"
            isLoading={isWorking}
            onClick={acceptWhileSignedIn}
          >
            {isWorking === true
              ? t('auth:acceptInvitation.accepting')
              : t('auth:acceptInvitation.acceptButton')}
          </Button>
        </div>
      ) : (
        <form onSubmit={signInThenAccept} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="accept-existing-email">{t('auth:emailLabel')}</Label>
            <Input id="accept-existing-email" value={preview.email} readOnly disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="accept-existing-password">{t('auth:passwordLabel')}</Label>
            <PasswordInput
              id="accept-existing-password"
              required
              value={password}
              placeholder={t('auth:passwordPlaceholder')}
              onChange={function updatePassword(changeEvent) {
                setPassword(changeEvent.target.value);
              }}
            />
          </div>
          <LegalConsentCheckbox
            audience={legalAudience}
            isChecked={hasAcceptedTerms}
            onChange={setHasAcceptedTerms}
          />
          <FormError message={errorMessage} />
          <Button type="submit" size="large" className="w-full" isLoading={isWorking}>
            {isWorking === true
              ? t('auth:acceptInvitation.accepting')
              : t('auth:acceptInvitation.signInButton')}
          </Button>
        </form>
      )}
    </>
  );
}

function FormError({ message }: { message: string }) {
  if (message.length === 0) {
    return null;
  }
  return (
    <div
      role="alert"
      className="flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
    >
      <Icon name="AlertCircle" />
      {message}
    </div>
  );
}

function InvalidInvitationMessage({ detail }: { detail: string }) {
  const { t } = useTranslation('auth');
  const description = detail.length > 0 ? detail : t('acceptInvitation.invalidDescription');
  return (
    <div className="text-center py-6">
      <div className="w-14 h-14 rounded-2xl bg-zooerrorbg flex items-center justify-center mx-auto mb-4">
        <Icon name="AlertCircle" className="text-xl text-zooerrortext" />
      </div>
      <h1 className="text-xl font-bold text-zooindigo">{t('acceptInvitation.invalidTitle')}</h1>
      <p className="text-sm text-zoomuted mt-2">{description}</p>
    </div>
  );
}
